import { ObjectHelpers } from "../../helpers/object-helpers";
import { StringHelpers } from "../../helpers/string-helpers";
import { IChartContainer } from "../../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../../models/chart-style/chart-style-settings.model";
import { TradeOrderDataModel } from "../../models/draw-trade-order/trade-order-data.model";
import { CurrentPositionViewModel } from "../../models/pnl/current-position-view-model";
import { TemporaryStopLossModel } from "../../models/stop-loss/temporary-stop-loss.model";
import { IComponent } from "../component.interface";
import { ExitOrderPlotLineComponent } from "./exit-order-plot-line.component";
import { InformationPopupComponent } from "../information-popup.component";
import { PnlComponent } from "../pnl.component";

export class DrawExitOrderComponent implements IComponent {
    public id = 'draw-exit-order-' + StringHelpers.newGuid();

    public exitOrderPlotLine: ExitOrderPlotLineComponent | null = null;
    public removeProfitTargetOcaGroups: Array<string> = [];
    public removeStopLossOcaGroups: Array<string> = [];
    public requestingProfitTarget = false;
    public requestingStopLoss = false;

    private base: IChartContainer;

    private tradeOrders: Array<TradeOrderDataModel> = [];
    private currentPositions: Array<CurrentPositionViewModel> = [];

    private prevTradeOrders: Array<TradeOrderDataModel> = [];
    private prevCurrentPositions: Array<CurrentPositionViewModel> = [];

    private chartStyle: ChartStyleSettingsModel | null = null;
    private canDraw = false;
    private chartLoaded = false;
    private rightEdge = false;
    private extreamSet = false;
    private isRemovingForSwitchingSymbol = false;
    private drawTimer: any;
    private redrawTimer: any;
    private setExtremeRetryTimer: any;
    private lastKnownElementWidth = 0;

    private temporaryStopLosses: Array<TradeOrderDataModel> = [];

    public initialize = false;

    constructor(base: IChartContainer) {
        this.base = base;

        if (!this.base.marketingWebChartMode) {
            this.canDraw = true;
        }

        this.subscribeChartDataService();
        this.subscribeChartEvents();
        this.onResize();
    }

    private remove() {
        this.isRemovingForSwitchingSymbol = true;
        this.removePlotLine();
        this.currentPositions = [];
        this.removeProfitTargetOcaGroups = [];
        this.removeStopLossOcaGroups = [];
        this.requestingProfitTarget = false;
        this.requestingStopLoss = false;
        this.chartLoaded = false;
        this.extreamSet = false;
        this.rightEdge = false;
        this.temporaryStopLosses = [];
        this.isRemovingForSwitchingSymbol = false;
        this.initialize = false;
        clearTimeout(this.setExtremeRetryTimer);
        this.lastKnownElementWidth = 0;
        this.disable();
    }

    public enable() {
        if (!this.base.marketingWebChartMode) return;

        if (this.canDraw) return;

        this.canDraw = true;
    }

    public disable() {
        if (!this.base.marketingWebChartMode) return;

        this.canDraw = false;
    }

    private addTemporaryStopLoss(model: TemporaryStopLossModel) {
        const nonChartMode = this.chartStyle?.nonNormalChartMode();
        if (nonChartMode) {
            return;
        }

        if (model.symbol != this.base.tiChartSeries.symbol) return;

        const index = this.temporaryStopLosses.findIndex(x => x.accountName == model.accountName && x.temporaryStopLoss.stopType == model.stopType);
        const m = new TradeOrderDataModel({
            accountName: model.accountName,
            price: model.price,
            temporaryStopLoss: model,
            isTemporary: true,
            entryTime: model.entryTime
        });;

        if (index > -1) {
            this.temporaryStopLosses[index] = m;
        } else {
            this.temporaryStopLosses.push(m);
        }

        this.addTemporaryStopLossPlotLines();
    }

    public removeTemporaryStopLoss(accountName: string) {
        if (this.temporaryStopLosses && this.temporaryStopLosses.some((x: TradeOrderDataModel) => x.accountName == accountName)) {
            this.exitOrderPlotLine.removeStopLosses();
            this.temporaryStopLosses = [];
        }
    }
    
    public get elementMaximumWidth(): number {
        let width = this.exitOrderPlotLine?.elementWidth ?? 0;
        if (this.exitOrderPlotLine?.profitTargetPlotLine) {
            const profitTargetWidth = this.exitOrderPlotLine.profitTargetPlotLine.elementWidth;
            if (profitTargetWidth > width) {
                width = profitTargetWidth;
            }
        }

        if (this.exitOrderPlotLine?.stopLossPlotLines?.length) {
            for (const stopLossPlotLine of this.exitOrderPlotLine.stopLossPlotLines) {
                if (stopLossPlotLine.elementWidth > width) {
                    width = stopLossPlotLine.elementWidth;
                }
            }
        }
        
        return width;
    }

    private subscribeChartDataService() {
        this.base.chartService.subscribeChartRequestingNewSymbolData(() => {
            this.remove();
        });
        this.base.chartService.subscribeDrawTradeOrdersData((tradeOrders: Array<TradeOrderDataModel>) => {
            const nonChartMode = this.chartStyle?.nonNormalChartMode();
            if (this.isRemovingForSwitchingSymbol || nonChartMode) {
                return;
            }

            this.tradeOrders = tradeOrders;
            this.displayProfitTargetAndStopLoss();

            this.prevTradeOrders = ObjectHelpers.copy(tradeOrders);
        });
        this.base.chartService?.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.chartStyle = chartStyle;

            const nonChartMode = this.chartStyle?.nonNormalChartMode();
            if (!this.chartStyle?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry || nonChartMode) {
                this.remove();
            }
        });
        this.base.chartService?.subscribe_AddTemporaryStopLoss((data: TemporaryStopLossModel) => {
            const nonChartMode = this.chartStyle?.nonNormalChartMode();
            if (this.isRemovingForSwitchingSymbol || nonChartMode) {
                return;
            }

            this.addTemporaryStopLoss(data);
        });
        this.base.chartService?.subscribe_RemoveTemporaryStopLoss((data: any) => {
            this.removeTemporaryStopLoss(data.accountName);
        });
        this.base.chartService?.subscribeCurrentPositionViewModel((currentPositions: Array<CurrentPositionViewModel>) => {
            if (this.isRemovingForSwitchingSymbol) {
                return;
            }

            this.currentPositions = currentPositions;
            this.update();

            this.prevCurrentPositions = ObjectHelpers.copy(currentPositions);
        });
    }

    private subscribeChartEvents() {
        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            this.exitOrderPlotLine?.onExtreamChange();
            this.reapplyExtremeIfWidthGrew();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            this.exitOrderPlotLine?.onRedraw();
            this.reapplyExtremeIfWidthGrew();
        });
        this.base.tiChartSeries?.subscribeLastPriceChange(this.id, (lastPrice: number) => {
            this.exitOrderPlotLine?.onLastPriceChange(lastPrice);
        });
        this.base.tiChartSeries?.subscribeChartLoad(this.id, () => {
            setTimeout(() => {
                this.chartLoaded = true;
                this.update();
            }, 500);
        });
        this.base.chartService.subscribe_CancelExecuteTradeImmediately(() => {
            this.exitOrderPlotLine?.cancelExecuteTradeImmediately();
        });
    }

    public update(forceHide?: boolean, rightEdge?: boolean) {
        const nonChartMode = this.chartStyle?.nonNormalChartMode();
        if (nonChartMode) {
            return;
        }
        
        if (rightEdge) {
            this.rightEdge = true;
            this.extreamSet = false;
        }

        if (!this.canDraw || !this.chartStyle?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry) return;

        if (this.exitOrderPlotLine) {
            const anyPlotLineMoving = this.exitOrderPlotLine.anyPlotLinesMoving();
            if (anyPlotLineMoving) {
                return;
            }

            if (this.exitOrderPlotLine.disabled && this.exitOrderPlotLine.removed) {
                const key = this.exitOrderPlotLine.position.getKey();
                const position = this.currentPositions.find(x => x.getKey() === key);
                if (position && position.shares != 0) {
                    return;
                }
            }
        }

        if (forceHide) {
            this.removePlotLine();
            this.removeLastPriceElements();
            return;
        }


        this.draw();
        clearTimeout(this.redrawTimer);
        this.redrawTimer = setTimeout(() => {
            this.base.tiChartSeries?.highStockChart?.redraw();
        }, 50);
        clearTimeout(this.drawTimer);
        this.drawTimer = setTimeout(() => {
            this.draw();
        }, 500);
    }

    private reapplyExtremeIfWidthGrew() {
        if (!this.rightEdge) return;
        const currentWidth = this.elementMaximumWidth;
        if (currentWidth > this.lastKnownElementWidth && this.extreamSet) {
            this.extreamSet = false;
            this.setExtremeIfExitOrderExists();
        }
        this.lastKnownElementWidth = currentWidth;
    }

    private setExtremeIfExitOrderExists() {
        if (!this.rightEdge || this.extreamSet) return;

        const ready = this.base.tiChartSeries?.ready;
        const widthReady = this.elementMaximumWidth > 0;

        if (!ready || !widthReady) {
            clearTimeout(this.setExtremeRetryTimer);
            this.setExtremeRetryTimer = setTimeout(() => this.setExtremeIfExitOrderExists(), 100);
            return;
        }

        this.base.tiChartSeries?.setExtreamToExitOrderIfExist();
        this.extreamSet = true;
        this.lastKnownElementWidth = this.elementMaximumWidth;
    }

    private draw() {
        const cannotDraw = !this.canDrawExitPlotLine();
        if (cannotDraw) {
            return;
        }

        this.cancelStopLossOrProfitTargetIfFilled();

        this.removePlotLine();
        this.createPlotLines();

        if (!this.initialize) {
            setTimeout(() => {
                this.initialize = true;
            }, 500);
        }
    }

    private canDrawExitPlotLine(): boolean {
        if (this.exitOrderPlotLine?.mouseHover) {
            return false;
        }

        const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
        if (!this.chartLoaded || !this.chartStyle?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry || this.base.tiChartSeries.preventDragging || popup.isActive()) {

            if (JSON.stringify(this.prevTradeOrders) != JSON.stringify(this.tradeOrders)
                || JSON.stringify(this.prevCurrentPositions) != JSON.stringify(this.currentPositions)) {
                return true;
            }

            return false;
        }

        return true;
    }

    private cancelStopLossOrProfitTargetIfFilled() {
        if (!this.exitOrderPlotLine) {
            return;
        }

        const positionExist = this.currentPositions.find(x => x.accountId == this.exitOrderPlotLine.position.accountId && x.shares > 0);
        if (positionExist) {
            return;
        }

        const tradeOrders = this.getSLOrPTTradeOrders().filter(x => x.statusText?.toLowerCase() == 'filled');
        for (const t of tradeOrders) {
            let tradeType = '';
            if (t.tradeType?.toLowerCase() == 'limit') {
                tradeType = 'stop';
            } else if (t.tradeType?.toLowerCase() == 'stop') {
                tradeType = 'limit';
            }

            const relatedOrders = this.getSLOrPTTradeOrders().filter(x =>
                x.tradeType?.toLowerCase() == tradeType &&
                x.statusText?.toLowerCase() == 'submitted');

            for (const relatedOrder of relatedOrders) {
                relatedOrder.statusText = 'canceled';
                this.base.chartService.requestRemoveChartTrade(relatedOrder.id);
            }
        }
    }

    private removePlotLine() {
        if (this.exitOrderPlotLine) {
            this.exitOrderPlotLine.remove(true);
            this.exitOrderPlotLine = null;
        }
    }

    private removeLastPriceElements() {
        this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.exit-order').forEach((el: HTMLElement) => {
            el.remove();
        });
        this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.profit-target').forEach((el: HTMLElement) => {
            el.remove();
        });
        this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.stop-loss').forEach((el: HTMLElement) => {
            el.remove();
        });
    }

    private createPlotLines() {
        const pnlComponent = this.base.getComponentsByType(PnlComponent)?.[0] as PnlComponent;
        let selectedAccount = '';
        if (pnlComponent) {
            selectedAccount = pnlComponent.selectedAccount;
        }

        let positionToAdd: CurrentPositionViewModel = null;
        for (const position of this.currentPositions) {
            if (position.shares != 0 && selectedAccount == position.accountName) {
                positionToAdd = position;
                break;
            }
        }

        if (positionToAdd) {
            const order = this.getOrder(positionToAdd);
            this.exitOrderPlotLine = new ExitOrderPlotLineComponent(this.base, this, positionToAdd, order);
            this.displayProfitTargetAndStopLoss();
            this.setExtremeIfExitOrderExists();
        } else {
            this.removeLastPriceElements();
        }
    }

    public displayProfitTargetAndStopLoss() {
        const cannotDraw = !this.canDrawExitPlotLine();
        if (!this.exitOrderPlotLine || cannotDraw) return;

        const anyPlotLineMoving = this.exitOrderPlotLine.anyPlotLinesMoving();
        if (anyPlotLineMoving) {
            return;
        }

        let profitTarget: TradeOrderDataModel | null = null;
        let stopLoss: TradeOrderDataModel | null = null;
        if (this.exitOrderPlotLine.isLong) {
            profitTarget = this.getOrder_LongProfitTarget();
            stopLoss = this.getOrder_LongStopLoss();
        } else {
            profitTarget = this.getOrder_ShortProfitTarget();
            stopLoss = this.getOrder_ShortStopLoss();
        }

        if (profitTarget && this.removeProfitTargetOcaGroups.indexOf(profitTarget.ocaGroup) == -1) {
            this.exitOrderPlotLine.createProfitTargetFromTradeOrder(profitTarget);

            this.requestingProfitTarget = false;
        } else {
            this.exitOrderPlotLine.removeProfitTarget();
            this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.profit-target').forEach((el: HTMLElement) => {
                el.remove();
            });
        }

        if (stopLoss && this.removeStopLossOcaGroups.indexOf(stopLoss.ocaGroup) == -1) {
            this.exitOrderPlotLine.createStopLossFromTradeOrder(stopLoss);
            this.requestingStopLoss = false;
        } else if (this.temporaryStopLosses?.length > 0) {
            this.addTemporaryStopLossPlotLines();
        } else {
            this.exitOrderPlotLine.removeStopLosses();
            this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.stop-loss').forEach((el: HTMLElement) => {
                el.remove();
            });
        }
    }

    private addTemporaryStopLossPlotLines() {
        const cannotDraw = !this.canDrawExitPlotLine();
        if (!this.exitOrderPlotLine?.position || cannotDraw) return;

        const anyPlotLineMoving = this.exitOrderPlotLine.anyPlotLinesMoving();
        if (anyPlotLineMoving) {
            return;
        }

        const stopLosees = this.temporaryStopLosses.filter(x => x.accountName == this.exitOrderPlotLine.position.accountName);
        this.exitOrderPlotLine.createTemporaryStopLoss(stopLosees);
        this.requestingStopLoss = true;
    }

    private getOrder(p: CurrentPositionViewModel): TradeOrderDataModel | null {
        const tradeOrders = this.tradeOrders.sort((a, b) => {
            return (b.entryTime?.valueOf() ?? -99999) - (a.entryTime?.valueOf() ?? -99999);
        });

        const tradeOrder = tradeOrders.find(x =>
            x.accountName == p.accountName &&
            (x.tradeType?.toLowerCase() == 'market' || x.statusText?.toLowerCase() == 'filled') &&
            !x.ocaGroup);
        return tradeOrder;
    }

    private getOrder_LongProfitTarget(): TradeOrderDataModel | null {
        const tradeOrder = this.getSLOrPTTradeOrders().find(x =>
            x.tradeType?.toLowerCase() == 'limit' &&
            !x.isLong);
        return tradeOrder;
    }

    private getOrder_LongStopLoss(): TradeOrderDataModel | null {
        const tradeOrder = this.getSLOrPTTradeOrders().find(x =>
            x.tradeType?.toLowerCase() == 'stop' &&
            !x.isLong);
        return tradeOrder;
    }

    private getOrder_ShortProfitTarget(): TradeOrderDataModel | null {
        const tradeOrder = this.getSLOrPTTradeOrders().find(x =>
            x.tradeType?.toLowerCase() == 'limit' &&
            x.isLong);
        return tradeOrder;
    }

    private getOrder_ShortStopLoss(): TradeOrderDataModel | null {
        const tradeOrder = this.getSLOrPTTradeOrders().find(x =>
            x.tradeType?.toLowerCase() == 'stop' &&
            x.isLong);
        return tradeOrder;
    }

    private getSLOrPTTradeOrders(): Array<TradeOrderDataModel> {
        if (!this.exitOrderPlotLine?.position) return [];

        const order = this.exitOrderPlotLine.model;
        const position = this.exitOrderPlotLine.position;
        const ocaGroup = position.getOCAGroupPrefix();
        let tradeOrders = this.tradeOrders
            .filter(x => x.accountName == position.accountName &&
                (x.ocaGroup.indexOf(ocaGroup) > -1
                    || (order && (order.tradeType?.toLowerCase() === 'market' || order.statusText?.toLowerCase() == 'filled') && x.ocaGroup == order.id)));

        if (order) {
            tradeOrders = tradeOrders.filter(x => x.ocaGroup == order.id);
        }
        
        return tradeOrders;
    }

    private resizeTimmer = null as any;
    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                this.update();
            }, 150);
        });
    }
}